import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { apiGet } from '../../api'
import { CheckboxField } from '../checkbox'
import type {
    ApiResponse,
    ChipProps,
    Filter,
    LoaderProps,
    NamedGroupItem,
    Props,
    RowProps,
    SelectAllRowProps,
    ServerItem,
    TargetRef,
    TargetType,
} from './ExecutionTargetSelect.types'
import './ExecutionTargetSelect.scss'

const ENDPOINT = '/api/automation/peers_group/'
const DEBOUNCE_MS = 400
const SCROLL_THRESHOLD_PX = 60
const ALL_TAB_LIMIT = 20
const DEFAULT_MENU_MAX_H = 360
const DEFAULT_MENU_GAP = 4

const ALL_TARGETS_REF: TargetRef = { type: 'all', id: 'all', label: 'Все устройства' }
const ALL_GROUPS_CHIP_REF: TargetRef = { type: 'group', id: '__all__', label: 'Все группы' }
const ALL_DYN_GROUPS_CHIP_REF: TargetRef = { type: 'dyn_group', id: '__all__', label: 'Все дин.группы' }

const FILTERS: { id: Filter; label: string }[] = [
    { id: 'all', label: 'Все' },
    { id: 'server', label: 'Устройства' },
    { id: 'group', label: 'Группы' },
    { id: 'dyn_group', label: 'Дин.группы' },
]

const ICON_BY_TYPE: Record<TargetType, string> = {
    all: 'rd-icon-all',
    server: 'rd-icon-screen',
    group: 'rd-icon-two-people',
    dyn_group: 'rd-icon-funnel',
}

const CHIP_MODIFIER_BY_TYPE: Record<TargetType, 'all' | 'online' | 'group'> = {
    all: 'all',
    server: 'online',
    group: 'group',
    dyn_group: 'group',
}

const OS_ICONS: Record<string, string> = {
    windows: 'rd-icon-os-windows',
    linux: 'rd-icon-os-linux',
    macos: 'rd-icon-os-macos',
    android: 'rd-icon-os-android',
}

const refKey = (r: TargetRef) => `${r.type}:${r.id}`
const refEq = (a: TargetRef, b: TargetRef) => a.type === b.type && a.id === b.id

const getServerId = (s: ServerItem) => s.id
const formatServerSubtitle = (s: ServerItem) => `${s.username ?? ''}@${s.hostname ?? 'None'}`
const getServerIcon = (s: ServerItem) =>
    (s.platform && OS_ICONS[s.platform]) || ICON_BY_TYPE.server

const getChipIcon = (ref: TargetRef): string => {
    if (ref.type === 'server' && ref.platform) {
        return OS_ICONS[ref.platform] ?? ICON_BY_TYPE.server
    }
    return ICON_BY_TYPE[ref.type]
}
const getServerStatusModifier = (s: ServerItem) =>
    s.status === 'online' ? 'online' : 'offline'

const serverToRef = (s: ServerItem): TargetRef => ({
    type: 'server',
    id: getServerId(s),
    label: getServerId(s),
    platform: s.platform ?? undefined,
})

const groupToRef = (g: NamedGroupItem): TargetRef => ({
    type: g.type,
    id: g.id,
    label: g.name,
})

const readPxVar = (el: Element, name: string, fallback: number): number => {
    const raw = getComputedStyle(el).getPropertyValue(name).trim()
    const n = parseFloat(raw)
    return Number.isFinite(n) ? n : fallback
}

function useTargetsQuery({ isOpen, search }: { isOpen: boolean; search: string }) {
    const [servers, setServers] = useState<ServerItem[]>([])
    const [groups, setGroups] = useState<NamedGroupItem[]>([])
    const [dynGroups, setDynGroups] = useState<NamedGroupItem[]>([])
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)

    const pageRef = useRef(1)
    const debounceRef = useRef<number | null>(null)
    const reqIdRef = useRef(0)

    const fetchAll = useCallback(async (q: string) => {
        pageRef.current = 1
        const reqId = ++reqIdRef.current
        setLoading(true)
        try {
            const resp = await apiGet<ApiResponse>(ENDPOINT, { search: q, page: 1 })
            if (reqId !== reqIdRef.current) return
            setServers(resp.servers?.results ?? [])
            setHasMore(resp.servers?.has_more ?? false)
            setGroups(resp.groups ?? [])
            setDynGroups(resp.dyn_groups ?? [])
        } catch (e) {
            console.error(e)
        } finally {
            if (reqId === reqIdRef.current) setLoading(false)
        }
    }, [])

    const fetchMoreServers = useCallback(async () => {
        if (loading || loadingMore || !hasMore) return
        const nextPage = pageRef.current + 1
        const reqId = ++reqIdRef.current
        setLoadingMore(true)
        try {
            const resp = await apiGet<ApiResponse>(ENDPOINT, {
                search,
                page: nextPage,
                kind: 'server',
            })
            if (reqId !== reqIdRef.current) return
            pageRef.current = nextPage
            setServers(prev => [...prev, ...(resp.servers?.results ?? [])])
            setHasMore(resp.servers?.has_more ?? false)
        } catch (e) {
            console.error(e)
        } finally {
            if (reqId === reqIdRef.current) setLoadingMore(false)
        }
    }, [search, loading, loadingMore, hasMore])

    useEffect(() => {
        fetchAll('')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isOpen) return
        if (debounceRef.current !== null) window.clearTimeout(debounceRef.current)
        setLoading(true)
        debounceRef.current = window.setTimeout(() => fetchAll(search), DEBOUNCE_MS)
        return () => {
            if (debounceRef.current !== null) window.clearTimeout(debounceRef.current)
            setLoading(false)
        }
    }, [search, isOpen, fetchAll])

    return { servers, groups, dynGroups, hasMore, loading, loadingMore, fetchMoreServers }
}

function useDropdownPlacement(
    containerRef: React.RefObject<HTMLElement | null>,
    isOpen: boolean,
) {
    const [placement, setPlacement] = useState<'down' | 'up'>('up')

    const updatePlacement = useCallback(() => {
        const el = containerRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const menuMaxH = readPxVar(el, '--execution-target-select-menu-max-h', DEFAULT_MENU_MAX_H)
        const gap = readPxVar(el, '--execution-target-select-menu-gap', DEFAULT_MENU_GAP)
        const spaceBelow = window.innerHeight - rect.bottom - gap
        const spaceAbove = rect.top - gap
        setPlacement(spaceBelow < menuMaxH && spaceAbove > spaceBelow ? 'up' : 'down')
    }, [containerRef])

    useEffect(() => {
        if (!isOpen) return
        const handler = () => updatePlacement()
        window.addEventListener('resize', handler)
        window.addEventListener('scroll', handler, true)
        return () => {
            window.removeEventListener('resize', handler)
            window.removeEventListener('scroll', handler, true)
        }
    }, [isOpen, updatePlacement])

    return { placement, updatePlacement }
}

function useOutsideClick(
    containerRef: React.RefObject<HTMLElement | null>,
    enabled: boolean,
    onOutside: () => void,
) {
    const cbRef = useRef(onOutside)
    cbRef.current = onOutside

    useEffect(() => {
        if (!enabled) return
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                cbRef.current()
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [enabled, containerRef])
}

function useTargetSelection(
    props: Props,
    groupRefs: TargetRef[],
    dynGroupRefs: TargetRef[],
) {
    const selected: TargetRef[] = props.multi
        ? props.value
        : props.value ? [props.value] : []

    const isPicked = (ref: TargetRef) => selected.some(s => refEq(s, ref))
    const isRowSelected = (ref: TargetRef) =>
        isPicked(ref) || (ref.type === 'server' && isPicked(ALL_TARGETS_REF))

    const allGroupsPicked = groupRefs.length > 0 && groupRefs.every(r => isPicked(r))
    const allDynPicked = dynGroupRefs.length > 0 && dynGroupRefs.every(r => isPicked(r))

    const pick = (ref: TargetRef) => {
        if (props.multi) {
            const current = props.value
            const exists = current.some(s => refEq(s, ref))
            if (ref.type === 'all') {
                props.onChange(exists ? [] : [ref])
            } else {
                const stripped = current.filter(s => s.type !== 'all')
                props.onChange(
                    exists
                        ? stripped.filter(s => !refEq(s, ref))
                        : [...stripped, ref],
                )
            }
        } else {
            const toggleOff = props.value && refEq(props.value, ref)
            props.onChange(toggleOff ? null : ref)
        }
    }

    const toggleSection = (refs: TargetRef[], allPicked: boolean) => {
        if (!props.multi || refs.length === 0) return
        const stripped = props.value.filter(s => s.type !== 'all' && !refs.some(r => refEq(r, s)))
        props.onChange(allPicked ? stripped : [...stripped, ...refs])
    }

    const removeChip = (ref: TargetRef) => {
        if (!props.multi) return
        props.onChange(props.value.filter(s => !refEq(s, ref)))
    }

    const removeAllOfType = (t: TargetType) => {
        if (!props.multi) return
        props.onChange(props.value.filter(s => s.type !== t))
    }

    const clear = () => {
        if (props.multi) props.onChange([])
        else props.onChange(null)
    }

    return {
        selected,
        isPicked,
        isRowSelected,
        allGroupsPicked,
        allDynPicked,
        pick,
        toggleSection,
        removeChip,
        removeAllOfType,
        clear,
    }
}

export default function ExecutionTargetSelect(props: Props) {
    const multi = props.multi === true

    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [filter, setFilter] = useState<Filter>('all')

    const containerRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const scrollByFilterRef = useRef<Partial<Record<Filter, number>>>({})

    const switchFilter = (next: Filter) => {
        if (next === filter) return
        if (listRef.current) scrollByFilterRef.current[filter] = listRef.current.scrollTop
        setFilter(next)
    }

    useLayoutEffect(() => {
        if (!isOpen || !listRef.current) return
        listRef.current.scrollTop = scrollByFilterRef.current[filter] ?? 0
    }, [filter, isOpen])

    // Scroll positions are only meaningful for the content they were captured on
    useLayoutEffect(() => {
        scrollByFilterRef.current = {}
        if (listRef.current) listRef.current.scrollTop = 0
    }, [search, isOpen])

    const {
        servers, groups, dynGroups,
        loading, loadingMore, fetchMoreServers,
    } = useTargetsQuery({ isOpen, search })

    const groupRefs = useMemo(() => groups.map(groupToRef), [groups])
    const dynGroupRefs = useMemo(() => dynGroups.map(groupToRef), [dynGroups])

    const selection = useTargetSelection(props, groupRefs, dynGroupRefs)
    const { selected, isPicked, isRowSelected, allGroupsPicked, allDynPicked } = selection

    const pick = (ref: TargetRef) => {
        selection.pick(ref)
        setSearch('')
        if (!multi) setIsOpen(false)
    }

    const toggleSection = (refs: TargetRef[], allPicked: boolean) => {
        selection.toggleSection(refs, allPicked)
        setSearch('')
    }

    const clearAll = (e: React.MouseEvent) => {
        e.stopPropagation()
        selection.clear()
        setSearch('')
        searchInputRef.current?.focus()
    }

    const findExactMatch = (val: string): TargetRef | null => {
        const server = servers.find(s => getServerId(s) === val)
        if (server) return serverToRef(server)
        const group = [...groups, ...dynGroups].find(g => g.name === val)
        if (group) return groupToRef(group)
        return null
    }

    const commitCustom = () => {
        const val = search.trim()
        if (!val) return
        pick(findExactMatch(val) ?? { type: 'server', id: val, label: val })
    }

    const { placement, updatePlacement } = useDropdownPlacement(containerRef, isOpen)
    useOutsideClick(containerRef, isOpen, () => setIsOpen(false))

    const openMenu = () => {
        if (props.disabled) return
        updatePlacement()
        setIsOpen(true)
        window.setTimeout(() => searchInputRef.current?.focus(), 0)
    }

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (filter !== 'server') return
        const el = e.currentTarget
        if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX) {
            fetchMoreServers()
        }
    }

    const hasValue = selected.length > 0
    const isInvalid = props.required && !hasValue
    const showError = props.error || (isInvalid && !isOpen)

    const showServers = filter === 'all' || filter === 'server'
    const visibleServers = filter === 'all'
        ? servers.slice(0, ALL_TAB_LIMIT)
        : servers
    const showGroups = filter === 'all' || filter === 'group'
    const visibleGroups = filter === 'all'
        ? groups.slice(0, ALL_TAB_LIMIT)
        : groups
    const showDyn = filter === 'all' || filter === 'dyn_group'
    const visibleDyn = filter === 'all'
        ? dynGroups.slice(0, ALL_TAB_LIMIT)
        : dynGroups

    const renderChips = () => {
        if (!props.multi) {
            return props.value && (
                <Chip ref_={props.value} onRemove={() => props.onChange(null)} />
            )
        }
        const individual = selected.filter(s =>
            !(allGroupsPicked && s.type === 'group')
            && !(allDynPicked && s.type === 'dyn_group')
        )
        return (
            <>
                {individual.map(ref => (
                    <Chip key={refKey(ref)} ref_={ref} onRemove={() => selection.removeChip(ref)} />
                ))}
                {allGroupsPicked && (
                    <Chip
                        ref_={ALL_GROUPS_CHIP_REF}
                        onRemove={() => selection.removeAllOfType('group')}
                    />
                )}
                {allDynPicked && (
                    <Chip
                        ref_={ALL_DYN_GROUPS_CHIP_REF}
                        onRemove={() => selection.removeAllOfType('dyn_group')}
                    />
                )}
            </>
        )
    }

    return (
        <div
            ref={containerRef}
            className={
                'execution-target-select'
                + (isOpen ? ' execution-target-select--opened' : '')
                + (showError ? ' execution-target-select--error' : '')
                + (props.disabled ? ' execution-target-select--disabled' : '')
            }
        >
            <div
                className="execution-target-select__field"
                onClick={openMenu}
            >
                <span className="execution-target-select__field-icon" aria-hidden={!loading} aria-label={loading ? 'Загрузка' : undefined}>
                    {loading
                        ? <Loader />
                        : <span className="execution-target-select__field-icon-img rd-icon-search" />}
                </span>
                <div className="execution-target-select__chips">
                    {renderChips()}
                    <input
                        ref={searchInputRef}
                        type="text"
                        className="execution-target-select__search-input"
                        placeholder={hasValue ? '' : (props.placeholder ?? 'Введите ID, имя устройства или группу')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onFocus={() => setIsOpen(true)}
                        onKeyDown={e => {
                            if (e.key === 'Escape') setIsOpen(false)
                            if (e.key === 'Enter' && search.trim()) {
                                e.preventDefault()
                                commitCustom()
                            }
                            if (e.key === 'Backspace' && !search && props.multi && selected.length > 0) {
                                selection.removeChip(selected[selected.length - 1])
                            }
                        }}
                    />
                </div>
                {(hasValue || search) && !props.disabled && (
                    <button
                        type="button"
                        className="execution-target-select__clear"
                        onClick={clearAll}
                        aria-label="Очистить"
                    >
                        <span className="rd-icon-cross" />
                    </button>
                )}
                <span className="execution-target-select__arrow rd-icon-arrow-down" aria-hidden />
            </div>

            {showError && typeof props.error === 'string' && (
                <span className="execution-target-select__error">{props.error}</span>
            )}
            {showError && !props.error && (
                <span className="execution-target-select__error">Пожалуйста, выберите устройство или группу</span>
            )}

            {props.name && selected.map(r => (
                <input key={refKey(r)} type="hidden" name={props.name} value={r.id} />
            ))}

            {isOpen && (
                <div className={'execution-target-select__menu execution-target-select__menu--' + placement}>
                    <div className="execution-target-select__filter" role="tablist">
                        {FILTERS.map(f => (
                            <button
                                key={f.id}
                                type="button"
                                role="tab"
                                aria-selected={filter === f.id}
                                className={
                                    'execution-target-select__filter-btn'
                                    + (filter === f.id ? ' execution-target-select__filter-btn--active' : '')
                                }
                                onClick={() => switchFilter(f.id)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {loading && <div className="execution-target-select__progress" aria-hidden />}

                    <div ref={listRef} className="execution-target-select__list" onScroll={handleScroll}>
                        {search.trim() && (
                            <div
                                className="execution-target-select__custom"
                                onClick={commitCustom}
                                role="option"
                                aria-selected={false}
                            >
                                <span className="execution-target-select__custom-plus">+</span>
                                <span className="execution-target-select__custom-text">
                                    Запустить на <b>{search.trim()}</b>
                                </span>
                            </div>
                        )}
                        {showServers && (
                            <>
                                <span className="execution-target-select__pin-marker" aria-hidden />
                                <SelectAllRow
                                    label="Все устройства"
                                    checked={isPicked(ALL_TARGETS_REF)}
                                    onToggle={() => pick(ALL_TARGETS_REF)}
                                />
                                {filter === 'all' && (
                                    <div className="execution-target-select__section">Устройства</div>
                                )}
                                {visibleServers.map(s => {
                                    const ref = serverToRef(s)
                                    return (
                                        <Row
                                            key={refKey(ref)}
                                            ref_={ref}
                                            multi={multi}
                                            selected={isRowSelected(ref)}
                                            onPick={() => pick(ref)}
                                            title={getServerId(s)}
                                            subtitle={formatServerSubtitle(s)}
                                            avatarModifier={getServerStatusModifier(s)}
                                            icon={getServerIcon(s)}
                                        />
                                    )
                                })}
                                {!loading && servers.length === 0 && (
                                    <div className="execution-target-select__empty">Нет устройств</div>
                                )}
                                {loadingMore && (
                                    <div className="execution-target-select__loading-row">
                                        <Loader small />
                                        <span>Загрузка…</span>
                                    </div>
                                )}
                            </>
                        )}

                        {showServers && (showGroups || showDyn) && <hr className="execution-target-select__divider" />}

                        {showGroups && (
                            <>
                                <span className="execution-target-select__pin-marker" aria-hidden />
                                <SelectAllRow
                                    label="Все группы"
                                    checked={allGroupsPicked}
                                    onToggle={() => toggleSection(groupRefs, allGroupsPicked)}
                                    disabled={groupRefs.length === 0}
                                />
                                {filter === 'all' && (
                                    <div className="execution-target-select__section">Группы</div>
                                )}
                                {visibleGroups.map(g => {
                                    const ref = groupToRef(g)
                                    return (
                                        <Row
                                            key={refKey(ref)}
                                            ref_={ref}
                                            multi={multi}
                                            selected={isRowSelected(ref)}
                                            onPick={() => pick(ref)}
                                            title={g.name}
                                            subtitle={`${g.peer_count} устройств`}
                                            avatarModifier="group"
                                        />
                                    )
                                })}
                                {!loading && groups.length === 0 && (
                                    <div className="execution-target-select__empty">Нет групп</div>
                                )}
                            </>
                        )}

                        {showGroups && showDyn && <hr className="execution-target-select__divider" />}

                        {showDyn && (
                            <>
                                <span className="execution-target-select__pin-marker" aria-hidden />
                                <SelectAllRow
                                    label="Все дин.группы"
                                    checked={allDynPicked}
                                    onToggle={() => toggleSection(dynGroupRefs, allDynPicked)}
                                    disabled={dynGroupRefs.length === 0}
                                />
                                {filter === 'all' && (
                                    <div className="execution-target-select__section">Дин.группы</div>
                                )}
                                {visibleDyn.map(g => {
                                    const ref = groupToRef(g)
                                    return (
                                        <Row
                                            key={refKey(ref)}
                                            ref_={ref}
                                            multi={multi}
                                            selected={isRowSelected(ref)}
                                            onPick={() => pick(ref)}
                                            title={g.name}
                                            subtitle={`${g.peer_count} устройств`}
                                            avatarModifier="group"
                                        />
                                    )
                                })}
                                {!loading && dynGroups.length === 0 && (
                                    <div className="execution-target-select__empty">Нет динамических групп</div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

function Row({ ref_, multi, selected, onPick, title, subtitle, avatarModifier, icon }: RowProps) {
    return (
        <div
            className={'execution-target-select__item' + (selected ? ' execution-target-select__item--selected' : '')}
            onClick={onPick}
            role="option"
            aria-selected={selected}
        >
            {multi && (
                <span onClick={e => e.stopPropagation()}>
                    <CheckboxField name="" checked={selected} onChange={onPick} />
                </span>
            )}
            <span className={`execution-target-select__avatar execution-target-select__avatar--${avatarModifier}`}>
                <span className={icon ?? ICON_BY_TYPE[ref_.type]} aria-hidden />
            </span>
            <div className="execution-target-select__body">
                <span className="execution-target-select__title" title={title}>{title}</span>
                {subtitle && <span className="execution-target-select__subtitle" title={subtitle}>{subtitle}</span>}
            </div>
        </div>
    )
}

function SelectAllRow({ label, checked, onToggle, disabled }: SelectAllRowProps) {
    return (
        <div
            className={
                'execution-target-select__item execution-target-select__item--all'
                + (checked ? ' execution-target-select__item--selected' : '')
                + (disabled ? ' execution-target-select__item--disabled' : '')
            }
            onClick={() => !disabled && onToggle()}
            role="option"
            aria-selected={checked}
        >
            <span onClick={e => e.stopPropagation()}>
                <CheckboxField name="" checked={checked} onChange={onToggle} />
            </span>
            <span className="execution-target-select__all-label">{label}</span>
        </div>
    )
}

const Loader = ({ small }: LoaderProps) => (
    <span
        className={'execution-target-select__spinner' + (small ? ' execution-target-select__spinner--sm' : '')}
        aria-hidden
    />
)

function Chip({ ref_, onRemove }: ChipProps) {
    const modifier = CHIP_MODIFIER_BY_TYPE[ref_.type]
    const iconClass = getChipIcon(ref_)
    return (
        <span className="execution-target-select__chip">
            <span className={`execution-target-select__chip-icon execution-target-select__chip-icon--${modifier}`}>
                <span className={iconClass} aria-hidden />
            </span>
            <span className="execution-target-select__chip-label" title={ref_.label ?? ref_.id}>{ref_.label ?? ref_.id}</span>
            <button
                type="button"
                className="execution-target-select__chip-remove"
                onClick={e => { e.stopPropagation(); onRemove() }}
                aria-label="Удалить"
            >
                <span className="rd-icon-cross" />
            </button>
        </span>
    )
}
